/**
 * Every exam this app simulates, by id.
 *
 * `structure.ts` and `certyfikat.ts` each describe their own and know nothing
 * of the other; this is the one place that knows about both, so a screen can
 * ask what a set's exam looks like without importing a particular exam.
 *
 * A set whose variant is missing falls back to telc, because every set that
 * predates variants is a telc set — an unknown id is a content mistake, not a
 * reason to render nothing.
 */
import { CERTYFIKAT_VARIANTS } from "./certyfikat.ts";
import { MODULE_BY_ID, TELC_VARIANT } from "./structure.ts";
import type { ExamFamilyId, ExamVariant, ExamVariantId, ModuleId } from "./exam-family.ts";

export const VARIANTS: ExamVariant[] = [TELC_VARIANT, ...CERTYFIKAT_VARIANTS];

const BY_ID = new Map(VARIANTS.map((v) => [v.id, v]));

export const variantById = (id: ExamVariantId | undefined): ExamVariant =>
  (id ? BY_ID.get(id) : undefined) ?? TELC_VARIANT;

export const familyOf = (id: ExamVariantId | undefined): ExamFamilyId =>
  variantById(id).family;

/** The families with sets in them, in the order they are offered. */
export const FAMILIES: ExamFamilyId[] = ["telc", "certyfikat"];

/**
 * How long a timed exam gives a block of this variant's paper, in minutes.
 *
 * The paper's own figure where the variant has one. A variant with no paper in
 * the library has `null` there; telc's minutes stand in until one is added, so
 * a block is never untimed — `tests/certyfikat-minutes.test.ts` holds that every variant
 * with sets in `content/` has its own figure for every block but listening,
 * whose clock is the recording.
 */
export const blockMinutes = (id: ExamVariantId | undefined, module: ModuleId): number =>
  variantById(id).modules.find((m) => m.id === module)?.minutes ?? MODULE_BY_ID[module].minutes;
